/**
 * Procedural textures and materials for dungeon visuals.
 * Adapted from https://github.com/majidmanzarpour/threejs-procedural-dungeon (MIT)
 */
import * as THREE from 'three';
import { WallCutout } from '../../rendering/WallCutout.js';

/**
 * @param {number} [maxAnisotropy=4]
 * @param {import('../../config/GameConfig.js').GameConfig['wallCutout']} [wallCutoutSettings]
 *   Tunables for the see-through holes punched in walls. Omit to leave walls
 *   sharing the plain stone material.
 */
export function createMaterialKit(maxAnisotropy = 4, wallCutoutSettings = null) {
  const maxAniso = maxAnisotropy;
  function makeCanvas(sz){ const c=document.createElement('canvas'); c.width=c.height=sz; return [c, c.getContext('2d')]; }
  const texRand = (function mulberry32(seed){ let a = seed >>> 0; return function(){ a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; })(0xC0FFEE);

function makeStoneTex(){
  const [cv,g] = makeCanvas(256);
  g.fillStyle='#c9c9c9'; g.fillRect(0,0,256,256);
  for(let i=0;i<2600;i++){
    const v = 170 + texRand()*110 | 0;
    g.fillStyle = 'rgba('+v+','+v+','+v+',0.16)';
    g.fillRect(texRand()*256, texRand()*256, 1+texRand()*3.4, 1+texRand()*3.4);
  }
  for(let i=0;i<420;i++){
    g.fillStyle = 'rgba(40,40,48,'+(0.05+texRand()*0.10).toFixed(3)+')';
    g.fillRect(texRand()*256, texRand()*256, 1+texRand()*2, 1+texRand()*2);
  }
  g.strokeStyle='rgba(30,30,36,0.20)'; g.lineWidth=1;
  for(let i=0;i<7;i++){
    let x=texRand()*256, y=texRand()*256;
    g.beginPath(); g.moveTo(x,y);
    for(let s=0;s<6;s++){ x+=(texRand()-0.5)*46; y+=(texRand()-0.5)*46; g.lineTo(x,y); }
    g.stroke();
  }
  const t = new THREE.CanvasTexture(cv);
  t.wrapS=t.wrapT=THREE.RepeatWrapping; t.colorSpace=THREE.SRGBColorSpace;
  t.anisotropy = Math.min(4, maxAniso);
  return t;
}
function makeCrackTex(){
  const [cv,g] = makeCanvas(128);
  g.lineCap='round';
  const branch=(x,y,a,w,d)=>{
    if(d<=0 || w<0.4) return;
    const len=9+texRand()*15, nx=x+Math.cos(a)*len, ny=y+Math.sin(a)*len;
    g.strokeStyle='rgba(255,255,255,'+(0.45+0.5*Math.min(1,w/3)).toFixed(2)+')'; g.lineWidth=w;
    g.beginPath(); g.moveTo(x,y); g.lineTo(nx,ny); g.stroke();
    branch(nx,ny, a+(texRand()-0.5)*1.0, w*0.76, d-1);
    if(texRand()<0.55) branch(nx,ny, a+(texRand()-0.5)*2.2, w*0.55, d-2);
  };
  for(let i=0;i<3;i++) branch(64,64, texRand()*6.28, 3, 6);
  return new THREE.CanvasTexture(cv);
}
function makeRuneTex(){
  const [cv,g] = makeCanvas(256);
  g.translate(128,128); g.lineCap='round';
  g.strokeStyle='rgba(255,255,255,0.85)';
  g.lineWidth=3; g.beginPath(); g.arc(0,0,104,0,6.2832); g.stroke();
  g.lineWidth=1.6; g.beginPath(); g.arc(0,0,76,0,6.2832); g.stroke();
  for(let i=0;i<20;i++){
    g.save(); g.rotate(i/20*6.2832); g.translate(90,0); g.rotate(1.5708);
    g.lineWidth=2.6; g.beginPath();
    let x=-4+texRand()*8, y=-7;
    g.moveTo(x,y);
    for(let s=0;s<3;s++){ x+=(texRand()-0.5)*12; y+=4+texRand()*4; g.lineTo(x,y); }
    g.stroke(); g.restore();
  }
  return new THREE.CanvasTexture(cv);
}
function makeSwirlTex(){
  const [cv,g] = makeCanvas(256);
  g.translate(128,128); g.lineCap='round';
  for(let arm=0;arm<3;arm++)
    for(let i=0;i<44;i++){
      const t0=i/44, a=arm*2.094 + t0*4.4, r=6+t0*112;
      g.strokeStyle='rgba(255,255,255,'+(0.55*(1-t0)).toFixed(3)+')';
      g.lineWidth=7*(1-t0)+1.5;
      g.beginPath(); g.arc(0,0,r,a,a+0.32); g.stroke();
    }
  const grd=g.createRadialGradient(0,0,0,0,0,36);
  grd.addColorStop(0,'rgba(255,255,255,0.9)'); grd.addColorStop(1,'rgba(255,255,255,0)');
  g.fillStyle=grd; g.beginPath(); g.arc(0,0,36,0,6.2832); g.fill();
  return new THREE.CanvasTexture(cv);
}
function makeShaftTex(){
  const [cv,g] = makeCanvas(64);
  const grd=g.createLinearGradient(0,0,0,64);
  grd.addColorStop(0,'rgba(255,255,255,0.7)'); grd.addColorStop(1,'rgba(255,255,255,0)');
  g.fillStyle=grd; g.fillRect(0,0,64,64);
  return new THREE.CanvasTexture(cv);
}
function makeGlowTex(){
  const [cv,g] = makeCanvas(128);
  const grd=g.createRadialGradient(64,64,3,64,64,62);
  grd.addColorStop(0,'rgba(255,255,255,0.85)');
  grd.addColorStop(0.35,'rgba(255,255,255,0.28)');
  grd.addColorStop(1,'rgba(255,255,255,0)');
  g.fillStyle=grd; g.beginPath(); g.arc(64,64,62,0,6.2832); g.fill();
  return new THREE.CanvasTexture(cv);
}
const TEX = { stone:makeStoneTex(), crack:makeCrackTex(), rune:makeRuneTex(), swirl:makeSwirlTex(), shaft:makeShaftTex(), glow:makeGlowTex() };

/* ================================================================
   MATERIAL KIT — named roles, shared across all instanced sets
   ================================================================ */
const matStone = new THREE.MeshStandardMaterial({map:TEX.stone, roughness:0.92, metalness:0.02});
const matTrim  = new THREE.MeshStandardMaterial({roughness:0.38, metalness:0.75});
const matGlow  = new THREE.MeshBasicMaterial({color:0xffffff});
matGlow.toneMapped = false;
const matCloth = new THREE.MeshLambertMaterial({side:THREE.DoubleSide});
const matIce   = new THREE.MeshStandardMaterial({roughness:0.16, metalness:0.02, transparent:true, opacity:0.88});
const matMoss  = new THREE.MeshLambertMaterial();
const matBark  = new THREE.MeshStandardMaterial({roughness:0.95, metalness:0});
const matCrackD = new THREE.MeshBasicMaterial({map:TEX.crack, transparent:true, blending:THREE.AdditiveBlending, depthWrite:false});
matCrackD.toneMapped = false;
const matRune  = new THREE.MeshBasicMaterial({map:TEX.rune, transparent:true, blending:THREE.AdditiveBlending, depthWrite:false, side:THREE.DoubleSide});
matRune.toneMapped = false;
const matPortal= new THREE.MeshBasicMaterial({map:TEX.swirl, transparent:true, blending:THREE.AdditiveBlending, depthWrite:false});
matPortal.toneMapped = false;
const matShaft = new THREE.MeshBasicMaterial({map:TEX.shaft, transparent:true, blending:THREE.AdditiveBlending, depthWrite:false, side:THREE.DoubleSide, opacity:0.13});
matShaft.toneMapped = false;
const matSkirt = new THREE.MeshBasicMaterial({map:TEX.glow, transparent:true, blending:THREE.AdditiveBlending, depthWrite:false, opacity:0.5});
matSkirt.toneMapped = false;

/* liquid surface shader: lava / ice / water / miasma via uMode */
const liquidMat = new THREE.ShaderMaterial({
  transparent:true, depthWrite:false,
  uniforms:{ uTime:{value:0}, uMode:{value:0}, uGlow:{value:1}, uOp:{value:1},
             uColA:{value:new THREE.Color(0x000000)}, uColB:{value:new THREE.Color(0xffffff)} },
  vertexShader:`
    attribute vec2 aE;
    attribute vec4 aM;
    varying vec2 vP, vE;
    varying vec4 vM;
    void main(){ vP = vec2(position.x, position.z); vE = aE; vM = aM;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader:`
    precision highp float;
    varying vec2 vP, vE;
    varying vec4 vM;
    uniform float uTime, uMode, uGlow, uOp;
    uniform vec3 uColA, uColB;
    float h21(vec2 p){ p=fract(p*vec2(123.34,456.21)); p+=dot(p,p+45.32); return fract(p.x*p.y); }
    float vnoise(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.0-2.0*f);
      float a=h21(i), b=h21(i+vec2(1,0)), c=h21(i+vec2(0,1)), d=h21(i+vec2(1,1));
      return mix(mix(a,b,f.x), mix(c,d,f.x), f.y); }
    float fbm(vec2 p){ float v=0.0, a=0.5;
      for(int i=0;i<4;i++){ v+=a*vnoise(p); p=p*2.03+11.7; a*=0.5; } return v; }
    void main(){
      vec3 col;
      if(uMode < 0.5){
        float n = fbm(vP*0.55 + vec2(uTime*0.045, uTime*0.021));
        float crust = smoothstep(0.40, 0.62, n);
        float veins = smoothstep(0.06, 0.0, abs(n-0.5));
        col = mix(uColB*1.6, uColA, crust);
        col += vec3(1.0,0.72,0.32) * veins * 0.9;
        col += uColB * 0.22 * (0.5 + 0.5*sin(uTime*1.7 + n*22.0));
      } else if(uMode < 1.5){
        float n = fbm(vP*0.8);
        float cr = smoothstep(0.47, 0.5, abs(fract(n*6.0)-0.5));
        col = mix(uColA, uColB, n);
        col += vec3(1.0) * cr * 0.16;
        float tw = step(0.994, h21(floor(vP*3.0) + floor(uTime*2.0)));
        col += vec3(0.8,0.95,1.0) * tw * 0.45;
      } else if(uMode < 2.5){
        float n = fbm(vP*0.7 + vec2(uTime*0.05, -uTime*0.035));
        float n2 = fbm(vP*1.3 - vec2(uTime*0.04, uTime*0.05));
        float caust = pow(1.0 - abs(n - n2), 6.0);
        col = mix(uColA, uColB, n*0.85) + vec3(0.5,0.9,0.8)*caust*0.35;
      } else {
        vec2 w = vP + 1.5*vec2(fbm(vP*0.35 + uTime*0.02), fbm(vP*0.35 - uTime*0.016));
        float n = fbm(w*0.5);
        col = mix(uColA, uColB, smoothstep(0.25, 0.75, n));
        col += uColB * 0.3 * smoothstep(0.6, 0.9, n);
      }
      /* soften true borders only: cooled crust for lava, depth falloff for
         water/ice, alpha fade for miasma */
      float e = 0.0;
      e = max(e, vM.x * smoothstep(0.26, 0.5, -vE.x));
      e = max(e, vM.y * smoothstep(0.26, 0.5,  vE.x));
      e = max(e, vM.z * smoothstep(0.26, 0.5, -vE.y));
      e = max(e, vM.w * smoothstep(0.26, 0.5,  vE.y));
      float aOut = uOp;
      if(uMode < 0.5)      col = mix(col, vec3(0.10,0.03,0.01), e*0.85);
      else if(uMode < 1.5) col *= (1.0 - 0.25*e);
      else if(uMode < 2.5) col *= (1.0 - 0.4*e);
      else                 aOut *= (1.0 - 0.55*e);
      gl_FragColor = vec4(col * (0.5 + uGlow), aOut);
    }`
});

/* ambient particle field: dust / embers / snow / wisps / spores (GPU) */
const partMat = new THREE.ShaderMaterial({
  transparent:true, depthWrite:false, blending:THREE.AdditiveBlending,
  uniforms:{ uTime:{value:0}, uRamp:{value:1}, uZoom:{value:2}, uKind:{value:0},
             uColor:{value:new THREE.Color(0xffffff)} },
  vertexShader:`
    attribute float aSeed;
    uniform float uTime, uRamp, uZoom, uKind;
    varying float vA;
    float h(float n){ return fract(sin(n*127.1)*43758.5453); }
    void main(){
      vec3 p = position;
      float s = aSeed, t, w;
      /* w = particle diameter in WORLD units; uZoom = device px per world
         unit, so sprites stay anchored to the scene across zoom levels */
      if(uKind < 0.5){            /* dust motes in light shafts */
        w = 0.05 + 0.05*h(s+3.1);
        p.x += sin(uTime*0.10 + s*17.0)*0.25;
        p.z += cos(uTime*0.08 + s*23.0)*0.25;
        p.y += 0.25*sin(uTime*0.13 + s*31.0);
        vA = 0.10 + 0.08*sin(uTime*0.5 + s*40.0);
      } else if(uKind < 1.5){     /* embers rising off lava + flames */
        w = 0.045 + 0.05*h(s+3.1);
        t = fract(uTime*(0.10 + 0.08*h(s)) + s);
        p.y += t*(1.1 + 0.9*h(s+5.0));
        p.x += sin(t*9.0 + s*50.0)*0.10;
        p.z += cos(t*8.0 + s*60.0)*0.10;
        vA = smoothstep(0.0,0.05,t)*(1.0-t)*(0.55 + 0.45*sin(uTime*10.0 + s*90.0));
      } else if(uKind < 2.5){     /* snowfall */
        w = 0.04 + 0.045*h(s+3.1);
        t = fract(uTime*(0.035 + 0.02*h(s)) + s);
        p.y += (1.0-t)*3.2;
        p.x += sin(uTime*0.5 + s*30.0)*0.3;
        p.z += cos(uTime*0.42 + s*36.0)*0.3;
        vA = 0.5*smoothstep(0.0,0.05,t)*smoothstep(1.0,0.95,t);
      } else if(uKind < 3.5){     /* wisps hovering over graves/candles */
        w = 0.09 + 0.07*h(s+3.1);
        p.x += sin(uTime*0.25 + s*44.0)*0.35;
        p.z += cos(uTime*0.21 + s*52.0)*0.35;
        p.y += 0.35 + 0.25*sin(uTime*0.4 + s*20.0);
        vA = 0.16 + 0.14*sin(uTime*1.3 + s*70.0);
      } else {                    /* spores drifting off moss/roots */
        w = 0.035 + 0.04*h(s+3.1);
        t = fract(uTime*0.03*(0.6 + h(s)) + s);
        p.y += t*1.3 + 0.08;
        p.x += sin(uTime*0.35 + s*25.0)*0.3;
        p.z += cos(uTime*0.3 + s*29.0)*0.3;
        vA = 0.35*smoothstep(0.0,0.08,t)*(1.0-t);
      }
      vA *= uRamp;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      gl_PointSize = max(w * uZoom, 1.2);
    }`,
  fragmentShader:`
    precision mediump float;
    uniform vec3 uColor;
    varying float vA;
    void main(){
      float d = length(gl_PointCoord - 0.5);
      float a = smoothstep(0.5, 0.12, d) * vA;
      gl_FragColor = vec4(uColor * (1.0 + 0.8*smoothstep(0.3, 0.0, d)), a);
    }`
});
partMat.toneMapped = false;

  /* Walls carry the cutout, so they need a material of their own: matStone is
     shared with the floors and every prop, and none of those should open up. */
  const wallCutout = wallCutoutSettings ? new WallCutout(matStone, wallCutoutSettings) : null;
  const matWall = wallCutout ? wallCutout.material : matStone;

  return {
    TEX,
    matStone, matWall, wallCutout,
    matTrim, matGlow, matCloth, matIce, matMoss, matBark,
    matCrackD, matRune, matPortal, matShaft, matSkirt,
    liquidMat, partMat,
    dispose() {
      for (const t of Object.values(TEX)) t.dispose?.();
      wallCutout?.dispose();
      for (const m of [matStone, matTrim, matGlow, matCloth, matIce, matMoss, matBark,
                       matCrackD, matRune, matPortal, matShaft, matSkirt, liquidMat, partMat]) {
        m.dispose?.();
      }
    }
  };
}
